import { useCallback, useEffect, useState } from "react";
import { Contact, Pencil, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { toE164 } from "@/lib/phone";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PAGE = 50;

function EditDialog({ c, onClose, onSaved }) {
  const { session } = useAuth();
  const [name, setName] = useState(c.name);
  const [num, setNum] = useState(c.whatsapp || "");
  const [err, setErr] = useState("");
  const save = async () => {
    const e164 = toE164(session.business.country, num.trim());
    if (!name.trim()) return setErr("Enter the customer's name");
    if (!e164) return setErr("Enter a valid WhatsApp number with country code");
    try { const { data } = await api.put(`/customers/${c.id}`, { name: name.trim(), whatsapp: e164 }); onSaved(data); toast.success("Customer saved"); }
    catch (e) { setErr(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="customer-edit-dialog">
        <DialogHeader><DialogTitle>Edit customer</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Name</Label><Input value={name} onChange={(e) => { setName(e.target.value); setErr(""); }} className="h-11" data-testid="customer-edit-name" /></div>
          <div className="space-y-1.5"><Label>WhatsApp number</Label><Input type="tel" value={num} onChange={(e) => { setNum(e.target.value); setErr(""); }} className="h-11" data-testid="customer-edit-whatsapp" /></div>
          {err && <p className="text-sm font-medium text-rose-600" data-testid="customer-edit-error">{err}</p>}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="h-11 rounded-xl border border-slate-300 px-4 text-sm font-semibold" data-testid="customer-edit-cancel">Cancel</button>
          <button onClick={save} className="h-11 rounded-xl bg-slate-900 px-5 text-sm font-semibold text-white" data-testid="customer-edit-save">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ c, onClose, onDone }) {
  const del = async () => {
    try { await api.delete(`/customers/${c.id}`); onDone(c.id); toast.success("Customer deleted"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="customer-delete-dialog">
        <DialogHeader><DialogTitle>Delete {c.name}?</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">They'll no longer be suggested when sending invoices. Past sales keep their receipts.</p>
        <DialogFooter>
          <button onClick={onClose} className="h-11 rounded-xl border border-slate-300 px-4 text-sm font-semibold" data-testid="customer-delete-cancel">Cancel</button>
          <button onClick={del} className="h-11 rounded-xl bg-rose-600 px-5 text-sm font-semibold text-white" data-testid="customer-delete-confirm">Delete</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Customers() {
  const { session } = useAuth();
  const allowed = ["owner", "manager"].includes(session?.user?.role);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);

  const load = useCallback(async (p, query) => {
    const { data } = await api.get("/customers", { params: { q: query, page: p, page_size: PAGE } });
    setRows((r) => (p === 1 ? data.items : [...(r || []), ...data.items]));
    setTotal(data.total);
    setPage(p);
  }, []);
  useEffect(() => {
    if (!allowed) return;
    const t = setTimeout(() => load(1, q.trim()).catch((e) => toast.error(errMsg(e))), 250);
    return () => clearTimeout(t);
  }, [q, load, allowed]);

  if (!allowed) return <div className="p-10 text-sm font-semibold text-slate-500" data-testid="customers-no-access">Only owners and branch managers can see customers.</div>;
  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-8" data-testid="customers-page">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold tracking-tight"><Contact className="h-7 w-7 text-amber-500" />Customers</h1>
          <p className="mt-1 text-sm text-slate-500">Saved when you send an invoice on WhatsApp. Shared across all branches.</p>
        </div>
        <label className="relative ml-auto w-full sm:w-72"><Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or number" className="h-11 pl-9" data-testid="customers-search" /></label>
      </div>
      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm" data-testid="customers-table">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">WhatsApp</th><th className="px-4 py-3">Last invoice</th><th className="px-4 py-3 text-right">Invoices</th><th className="px-4 py-3" /></tr>
          </thead>
          <tbody>
            {(rows || []).map((c) => (
              <tr key={c.id} className="border-b border-slate-100 last:border-0" data-testid={`customer-row-${c.id}`}>
                <td className="px-4 py-3 font-semibold text-slate-900">{c.name}</td>
                <td className="money px-4 py-3 text-slate-700">{c.whatsapp || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{c.last_invoice_at ? new Date(c.last_invoice_at).toLocaleString() : "—"}</td>
                <td className="money px-4 py-3 text-right">{c.invoice_count}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => setEdit(c)} aria-label="Edit" className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100" data-testid={`customer-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => setDel(c)} aria-label="Delete" className="grid h-9 w-9 place-items-center rounded-lg text-rose-500 hover:bg-rose-50" data-testid={`customer-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows && !rows.length && <p className="p-8 text-center text-sm text-slate-500" data-testid="customers-empty">{q ? "No customers match." : "No customers yet. They're saved when you send an invoice on WhatsApp with a name."}</p>}
      </div>
      {rows && rows.length < total && <button onClick={() => load(page + 1, q.trim())} className="mt-4 h-11 rounded-xl border border-slate-300 px-5 text-sm font-semibold" data-testid="customers-load-more">Load more</button>}
      {edit && <EditDialog c={edit} onClose={() => setEdit(null)} onSaved={(d) => { setRows((r) => r.map((x) => (x.id === d.id ? d : x))); setEdit(null); }} />}
      {del && <DeleteDialog c={del} onClose={() => setDel(null)} onDone={(id) => { setRows((r) => r.filter((x) => x.id !== id)); setTotal((t) => t - 1); setDel(null); }} />}
    </div>
  );
}
